import React from "react";
import { fonts } from "../../../config";
import { masks, validations } from "../../../utils";
import { StylesForm } from "../styles";
import Grid from "@mui/material/GridLegacy";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  ClearButton,
  SubmitButton,
  InputText,
  InputSelect,
  InputPhone,
} from "../../../components/newComponents";

const schema = z
  .object({
    name: z
      .string()
      .min(1, "Campo Obrigatório")
      .regex(
        /^[A-zÀ-Ÿ][A-zÀ-ÿ']+\s([A-zÀ-ÿ']\s?)*[A-zÀ-Ÿ][A-zÀ-ÿ']+$/,
        "Nome inválido (ex: João Silva)"
      ),
    docType: z.number(),
    docValue: z.string().default(""),
    email: z.string().min(1, "Campo Obrigatório").email("E-mail inválido"),
    phone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.docType === 1) {
      const err = validations.validateCPF(data.docValue || "");
      if (err !== null) {
        ctx.addIssue({
          code: "custom",
          message: err || "CPF inválido",
          path: ["docValue"],
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

function TenantForm({ data, onSubmit, initialValues }: { data: any; onSubmit: (v: any) => void; initialValues?: any }) {
  const { control, handleSubmit, watch, reset, setValue, trigger } =
    useForm<FormValues>({
      resolver: zodResolver(schema) as any,
      defaultValues: {
        name: initialValues?.name ?? data?.primaryGuest?.name ?? "",
        docType: initialValues?.docType ?? (data?.primaryGuest?.document ? 1 : data?.primaryGuest?.passport ? 2 : 1),
        docValue: initialValues?.docValue ?? data?.primaryGuest?.document ?? data?.primaryGuest?.passport ?? "",
        email: initialValues?.email ?? data?.primaryGuest?.email ?? "",
        phone: initialValues?.phone ?? data?.primaryGuest?.phone ?? "",
      },
    });

  const watchedDocType = watch("docType");

  const onSubmitHandler = (formValues: FormValues) => {
    onSubmit(formValues);
  };

  return (
    <StylesForm.HeaderSteps>
      <form onSubmit={handleSubmit(onSubmitHandler)} style={{ width: "100%" }}>
        <Grid alignItems="flex-end" container spacing={1}>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label="NOME COMPLETO"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>

          {/* Tipo do Documento */}
          <Grid item xs={12} md={6} lg={6}>
            <Controller
              name="docType"
              control={control}
              render={({ field, fieldState }) => (
                <InputSelect
                  label="TIPO DO DOCUMENTO"
                  error={fieldState.error?.message}
                  value={field.value}
                  options={[
                    { value: 1, label: "CPF" },
                    { value: 2, label: "Passaporte" },
                  ]}
                  onChange={(value) => {
                    field.onChange(Number(value));
                    setValue("docValue", "");
                    trigger("docValue");
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Controller
              name="docValue"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label={watchedDocType === 1 ? "CPF" : "PASSAPORTE"}
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={(e) => {
                    const value =
                      watchedDocType === 1
                        ? masks.CPF(e.target.value)
                        : e.target.value;
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label="E-MAIL"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  readOnly
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <InputPhone
                  label="Telefone"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="TELEFONE"
                />
              )}
            />
          </Grid>
        </Grid>

        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            marginBottom: "16px",
            marginTop: "16px",
          }}
        >
          <ClearButton
            fullWidth={false}
            variant="text"
            type="button"
            onClick={() => reset({ name: "", docType: 1, docValue: "", email: "", phone: "" })}
          >
            Limpar Campos
          </ClearButton>
          <SubmitButton fullWidth={false} type="submit">
            Confirmar
          </SubmitButton>
        </div>
      </form>
    </StylesForm.HeaderSteps>
  );
}

export default TenantForm;
